import React, { useRef, useState, useEffect, lazy, Suspense } from "react";
import "../styles/pages/Dashboard.css";
import "../styles/PhylogeneticTree.css";

// Lazy load chart components for better performance
const CumulativeSumByDate = lazy(() => import("../components/CumulativeSumByDate"));
const SequenceCollectionByDate = lazy(() => import("../components/SequenceCollectionByDate"));
const VisualizationMap = lazy(() => import("../components/VisualizationDashboard"));
const PhylogeneticTree = lazy(() => import("../components/PhylogeneticTree"));

const Dashboard = ({ filters }) => {
  const [activeChart, setActiveChart] = useState("phylogenetic");
  const [loadedCharts, setLoadedCharts] = useState(new Set(["phylogenetic"])); // Track which charts have been loaded
  const [globalFilters, setGlobalFilters] = useState({
    hosts: [],
    dateRange: { start: "2016-01-01", end: "2025-05-23" }
  });

  // Chart section refs for scrolling
  const sectionRefs = {
    phylogenetic: useRef(null),
    cumulative: useRef(null),
    sequence: useRef(null),
    map: useRef(null)
  };

  // Chart definitions
  const charts = [
    { id: "phylogenetic", title: "Phylogenetic Tree" },
    { id: "cumulative", title: "Cumulative Isolates" },
    { id: "sequence", title: "Sequence Collection" },
    { id: "map", title: "Isolates Heat Map" }
  ];

  // Chart content definitions
  const chartContents = {
    phylogenetic: {
      component: (
        <PhylogeneticTree
          filters={globalFilters}
          selectedSource="humanCowHost"
          onFilterChange={(newFilters) => setGlobalFilters({ ...globalFilters, ...newFilters })}
        />
      )
    },
    cumulative: {
      component: <CumulativeSumByDate filters={globalFilters} />
    },
    sequence: {
      component: <SequenceCollectionByDate filters={globalFilters} />
    },
    map: {
      component: <VisualizationMap filters={globalFilters} />
    }
  };

  // Handle URL hash changes (for navbar navigation)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && sectionRefs[hash]) {
        setActiveChart(hash);
        // Add timeout to ensure DOM is ready, then scroll with navbar offset
        setTimeout(() => {
          const element = sectionRefs[hash].current;
          if (element) {
            const navbarHeight = 70; // Adjust this value based on your navbar height
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - navbarHeight;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth"
            });
          }
        }, 100);
      }
    };

    // Handle initial load
    const currentHash = window.location.hash.replace('#', '');
    if (currentHash && sectionRefs[currentHash]) {
      setActiveChart(currentHash);
      setTimeout(() => {
        const element = sectionRefs[currentHash].current;
        if (element) {
          const navbarHeight = 70; // Adjust this value based on your navbar height
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - navbarHeight;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
        }
      }, 200); // Longer timeout for initial load
    }

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Intersection Observer to lazy load charts as they approach viewport
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "200px", // Load charts 200px before they enter viewport
      threshold: 0.01
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const chartId = entry.target.id;
          setLoadedCharts(prev => new Set([...prev, chartId]));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all chart sections
    charts.forEach(chart => {
      const element = sectionRefs[chart.id]?.current;
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Detect which chart is currently in view while scrolling
  useEffect(() => {
    const handleScroll = () => {
      const navbarHeight = 70; // Same navbar height as above
      const scrollPosition = window.scrollY + navbarHeight + (window.innerHeight / 2);

      for (const chart of charts) {
        const element = sectionRefs[chart.id].current;
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = window.scrollY + rect.top;
          const elementBottom = elementTop + rect.height;

          if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
            if (activeChart !== chart.id) {
              setActiveChart(chart.id);
              // Update URL hash without triggering scroll
              window.history.replaceState(null, null, `#${chart.id}`);
            }
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeChart, charts]);

  // Loading placeholder component
  const LoadingPlaceholder = () => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      color: '#6b7280',
      fontSize: '1rem'
    }}>
      <div>
        <div className="spinner" style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e5e7eb',
          borderTopColor: '#3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem'
        }}></div>
        Loading chart...
      </div>
    </div>
  );


  return (
    <div className="dashboard-page">
      {/* Continuous chart sections */}
      <div className="dashboard-content">
        {charts.map(chart => (
          <div
            key={chart.id}
            ref={sectionRefs[chart.id]}
            className={`chart-section ${chart.id}-section`}
            id={chart.id}
          >
            <div className="chart-container">
              {loadedCharts.has(chart.id) ? (
                <Suspense fallback={<LoadingPlaceholder />}>
                  {chartContents[chart.id].component}
                </Suspense>
              ) : (
                <div style={{
                  minHeight: '600px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#9ca3af',
                  fontSize: '0.875rem'
                }}>
                  Scroll to load {chart.title}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;